import { AxiosError } from 'axios';
import React, { useEffect, useRef, useState } from 'react';

import { NewWindowLink } from '@amzn/imdb-shared-meridian-components/components/link';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import { ListFlagHistoryAction, ListFlagHistoryEvent } from '../../../listoramaAdmin-api/generated-src/api';
import ListORamaAdminApiFactory from '../../../listoramaAdmin-api/ListORamaAdminApiFactory';

export const FLAG_HISTORY_PAGE_SIZE = 50;

export const FLAG_HISTORY_EMPTY_MESSAGE =
    'No flag history recorded. Flags last changed through the legacy flag APIs have no history.';

export const FLAG_HISTORY_TEST_ID = {
    LOAD_MORE_BUTTON: 'flag-history-load-more-button',
    RETRY_BUTTON: 'flag-history-retry-button'
};

// The ticket ID is operator free text, so it is encoded rather than trusted as a URL path segment.
export const getTicketUrl = (ticketId: string) => `https://t.corp.amazon.com/${encodeURIComponent(ticketId)}`;

/**
 * The flag's state before this event, which is what makes a correction legible: "SPAM removed by admin:jdoe"
 * plus "Before: SPAM set by IMDbSpamClassifier:v3" reads as a classifier false positive.
 */
export const describePreviousState = (event: ListFlagHistoryEvent): string => {
    const hasPreviousState = typeof event.previousIsActive === 'boolean';
    if (hasPreviousState && event.previousSource) {
        return `Before: ${event.flag} ${event.previousIsActive ? 'set' : 'cleared'} by ${event.previousSource}`;
    }
    if (hasPreviousState) {
        return `Before: ${event.flag} ${event.previousIsActive ? 'set' : 'not set'}`;
    }
    if (event.previousSource) {
        return `Before: last changed by ${event.previousSource}`;
    }
    return 'Before: no prior attribution';
};

/**
 * What happened to the flag. ListORama also records a human re-confirming a state the flag already had (attribution
 * updated, flag unchanged); that event's previous state equals its result, so it reads as a confirmation rather than
 * an add or remove that never happened.
 */
export const describeChange = (event: ListFlagHistoryEvent): string => {
    const isSet = event.action === ListFlagHistoryAction.Added;
    if (event.previousIsActive === isSet) {
        return `${event.flag} confirmed ${isSet ? 'present' : 'absent'} (flag unchanged)`;
    }
    return `${event.flag} ${isSet ? 'added' : 'removed'}`;
};

// The backend puts its reason in the response body (e.g. "List ID: ls123 not found."); prefer it to axios's.
const describeError = (error: AxiosError): string =>
    typeof error.response?.data === 'string' && error.response.data ? error.response.data : error.message;

interface FlagHistoryState {
    events: ListFlagHistoryEvent[];
    nextToken?: string;
    fetching: boolean;
    error?: AxiosError;
}

export interface FlagHistorySectionProps {
    listId: string;
}

/**
 * A list's whole moderation story on listDetails, newest first: who added or removed each flag, why, and what
 * the flag looked like before. Older events load a page at a time via the API's continuation token.
 */
export const FlagHistorySection: React.FC<FlagHistorySectionProps> = (props: FlagHistorySectionProps) => {
    const { listId } = props;
    const [history, setHistory] = useState<FlagHistoryState>({ events: [], fetching: true });
    // Only the latest request may update the timeline. A response that lands after a newer request started is
    // stale and dropped: comparing list IDs alone would accept a slow response from an earlier visit to the same
    // list (A, then B, then back to A) and overwrite or duplicate the current history.
    const latestRequestId = useRef(0);

    const fetchPage = async (nextToken?: string) => {
        const requestId = ++latestRequestId.current;
        setHistory((prevState) => ({ ...prevState, fetching: true, error: undefined }));
        try {
            const response = await ListORamaAdminApiFactory().listFlagHistory.adminGetListFlagHistory(
                listId,
                FLAG_HISTORY_PAGE_SIZE,
                nextToken
            );
            if (requestId !== latestRequestId.current) {
                return;
            }
            setHistory((prevState) => ({
                // Pages arrive newest first, so a later page holds older events and appends below.
                events: nextToken ? [...prevState.events, ...response.data.events] : response.data.events,
                nextToken: response.data.nextToken || undefined,
                fetching: false
            }));
        } catch (e) {
            if (requestId !== latestRequestId.current) {
                return;
            }
            setHistory((prevState) => ({ ...prevState, fetching: false, error: e as AxiosError }));
        }
    };

    useEffect(() => {
        setHistory({ events: [], fetching: true });
        fetchPage();
    }, [listId]);

    if (history.fetching && history.events.length === 0) {
        return (
            <Row spacing='200'>
                <Loader size='small' />
                <Text type='b300'>Loading flag history...</Text>
            </Row>
        );
    }

    return (
        <Column spacing='300'>
            {history.error && (
                <Alert type='error' size='small'>
                    <Row spacing='300' wrap='down'>
                        <Text type='b300'>{`Could not load flag history: ${describeError(history.error)}`}</Text>
                        <Button
                            size='small'
                            type='tertiary'
                            // Retries the page that failed: the first page, or the older page behind nextToken.
                            onClick={() => fetchPage(history.nextToken)}
                            data-testid={FLAG_HISTORY_TEST_ID.RETRY_BUTTON}
                        >
                            Retry
                        </Button>
                    </Row>
                </Alert>
            )}
            {!history.error && history.events.length === 0 && (
                <Text type='b300' color='secondary'>
                    {FLAG_HISTORY_EMPTY_MESSAGE}
                </Text>
            )}
            {history.events.length > 0 && (
                // Explicit roles: Safari drops list semantics from a list styled with list-style: none.
                <ol
                    role='list'
                    aria-label='Flag history, newest first'
                    style={{ listStyle: 'none', margin: 0, padding: 0 }}
                >
                    {history.events.map((event, index) => (
                        <FlagHistoryEntry
                            key={`${index}|${event.eventTimestamp}|${event.flag}|${event.action}`}
                            event={event}
                        />
                    ))}
                </ol>
            )}
            {history.nextToken && !history.error && (
                <Row>
                    <Button
                        size='small'
                        type='tertiary'
                        disabled={history.fetching}
                        onClick={() => fetchPage(history.nextToken)}
                        data-testid={FLAG_HISTORY_TEST_ID.LOAD_MORE_BUTTON}
                    >
                        {history.fetching ? 'Loading older events...' : 'Load older events'}
                    </Button>
                </Row>
            )}
        </Column>
    );
};

interface FlagHistoryEntryProps {
    event: ListFlagHistoryEvent;
}

const FlagHistoryEntry: React.FC<FlagHistoryEntryProps> = (props: FlagHistoryEntryProps) => {
    const { event } = props;

    return (
        <li role='listitem' style={{ padding: '8px 0', borderBottom: '1px solid #e7e9e9' }}>
            <Column spacing='100'>
                <Row spacing='200' wrap='down' alignmentVertical='center'>
                    <Text type='h100'>{describeChange(event)}</Text>
                    <Text type='b200' color='secondary'>
                        {`by ${event.source} on ${event.eventTimestamp}`}
                    </Text>
                    {event.isOverride && (
                        <Tooltip
                            position='end'
                            title='A human override: automated re-classification will not silently undo this change.'
                        >
                            <Box type='outline' backgroundColor='SteelBlue'>
                                OVERRIDE
                            </Box>
                        </Tooltip>
                    )}
                    {event.reasonCode && <Text type='b200'>{`Reason: ${event.reasonCode}`}</Text>}
                </Row>
                <Text type='b200' color='secondary'>
                    {describePreviousState(event)}
                </Text>
                {event.note && (
                    <Text type='b300'>
                        <strong>Note: </strong>
                        <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{event.note}</span>
                    </Text>
                )}
                {event.ticketId && (
                    <Row spacing='100'>
                        <Text type='b200'>Ticket:</Text>
                        <NewWindowLink
                            url={getTicketUrl(event.ticketId)}
                            text={event.ticketId}
                            rel='noopener noreferrer'
                        />
                    </Row>
                )}
                {event.wasPublic === false && (
                    <Text type='b200' color='secondary'>
                        List was private at the time
                    </Text>
                )}
            </Column>
        </li>
    );
};
